import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AnalysisVerificationState } from '@aica/api-types/schemas';
import { NARRATIVE_SECTION_LABEL_KEYS } from '@/lib/records/narrative-sections';
import {
  isVerificationWithheld,
  VERIFICATION_DETAIL_ID,
  verificationTitle,
} from '@/lib/records/verification-title';
import { cn } from '@/lib/utils';

/**
 * VerificationNotice — разбор состояния проверки.
 *
 * Стоит под карточкой общего балла и появляется по нажатию на значок
 * (`VerificationBadge`), а не сам по себе: до #752 это был блок во всю ширину
 * НАД оценками, и при 82% заблокированных анализов он висел на четырёх записях
 * из пяти — предупреждение, которое видно всегда, читается как часть рамки.
 *
 * Показывается в двух случаях, а не в одном: гейт закрыт ИЛИ верификатор
 * возражал против отдельных утверждений. Второй случай появился с #752 —
 * нарекание к прозе больше не закрывает автоматизацию, и анализ бывает
 * `verified` С замечаниями. Решает, показывать ли, панель; здесь только текст.
 *
 * Список замечаний раскрыт сразу: до него дошли нажатием на значок, и второй
 * тумблер сделал бы неправдой обещание доков «нажатие раскрывает сами
 * утверждения». Свернуть список по-прежнему можно.
 *
 * What it says about CRM describes what the GATE did, not what the CRM now
 * holds. Two traps sit here. "No CRM changes" is false — the report itself IS
 * delivered to the CRM, carrying its own notice; what verification withholds
 * is the automatic part. And "fields were not filled in", the sentence the
 * report e-mail carries (`i18n/analysis-report.ts`), stops being true the
 * moment a human approves the review: the delivery is replayed with
 * `manuallyApproved` and the fields ARE filled, while this analysis still
 * reads `review_required`. Withholding is what verification did, and it stays
 * true afterwards — a person released those changes, not the automation.
 */
export function VerificationNotice({
  verification,
}: {
  verification: AnalysisVerificationState;
}) {
  const { t } = useTranslation(['records', 'common']);
  /** Раскрыт по умолчанию — см. шапку файла: значок и есть орган раскрытия. */
  const [expanded, setExpanded] = useState(true);
  const { verificationStatus, reviewReasonCodes, narrativeIssues } = verification;

  // Один словарь заголовков на значок, разбор и печать
  // (`lib/records/verification-title.ts`): состояние, названное по-разному на
  // соседних элементах одного экрана, читается как два разных события.
  const title = verificationTitle(verificationStatus, (key, fallback) =>
    t(key, fallback)
  );
  const withheld = isVerificationWithheld(verificationStatus);

  return (
    <div
      // Значок объявляет этот id в `aria-controls`: он лежит в шапке карточки
      // балла, то есть двумя соседями выше, и без пары читалке некуда перейти.
      id={VERIFICATION_DETAIL_ID}
      data-testid="verification-notice"
      className={cn(
        'rounded-card border border-warning bg-warning-dim p-3',
        'flex items-start gap-3'
      )}
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <div className="min-w-0 flex-1 space-y-2">
        <p className="text-sm font-medium text-fg">{title}</p>

        {reviewReasonCodes.length > 0 && (
          <p className="text-xs leading-relaxed text-fg-muted">
            {reviewReasonCodes
              // Reason codes are already translated for the Review Queue —
              // reuse those strings rather than mint a second vocabulary. The
              // `common:` prefix is not decoration: they live in common.json,
              // and a bare key would depend on cross-namespace fallback.
              .map((code) => t(`common:reviewQueue.reasons.${code}`, code))
              .join(' · ')}
          </p>
        )}

        <p className="text-xs leading-relaxed text-fg-muted">
          {withheld &&
            t(
              'records.verificationNotice.body',
              "Verification withheld this record's automatic CRM changes — filling card fields and creating follow-up tasks."
            )}
          {/* Only when there IS a list — an analysis stored before the verdict
              was persisted has nothing "below" to check. */}
          {narrativeIssues.length > 0 &&
            `${withheld ? ' ' : ''}${t(
              'records.verificationNotice.issuesHint',
              'Check the statements below against the transcript before acting on them.'
            )}`}
        </p>

        {narrativeIssues.length > 0 && (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-expanded={expanded}
              data-testid="verification-notice-toggle"
              className={cn(
                'flex items-center gap-1 text-xs font-medium text-fg',
                'hover:text-accent focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring focus-visible:ring-offset-1'
              )}
            >
              {expanded ? (
                <ChevronDown className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5" />
              )}
              {t('records.verificationNotice.issuesToggle', 'Unconfirmed statements')} (
              {narrativeIssues.length})
            </button>

            {expanded && (
              <ul className="space-y-2" data-testid="verification-notice-issues">
                {narrativeIssues.map((issue, index) => {
                  const sectionKey = NARRATIVE_SECTION_LABEL_KEYS.get(issue.sectionId);
                  // `bg-bg-elevated`, не `bg-surface`: `--color-surface` не
                  // объявлен, и такой класс молча ничего не красит — та же
                  // ловушка, что с `bg-bg-muted`.
                  return (
                    <li
                      key={`${issue.sectionId}-${index}`}
                      className="rounded-card border border-border bg-bg-elevated p-2 text-xs"
                    >
                      <p className="font-medium text-fg">
                        {sectionKey ? t(sectionKey) : issue.sectionId} ·{' '}
                        <span className="font-normal text-fg-muted">
                          {t(
                            `records.verificationNotice.issueTypes.${issue.issueType}`,
                            issue.issueType
                          )}
                        </span>
                      </p>
                      <p className="mt-1 text-fg">{issue.draftExcerpt}</p>
                      {issue.evidenceQuote && (
                        <p className="mt-1 italic text-fg-muted">
                          {t('records.verificationNotice.inCall', 'In the conversation')}:{' '}
                          “{issue.evidenceQuote}”
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
